import { useState, useMemo } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useMetricsSessions, useModeratorMetrics, useAgentMetrics, useContentAnalysis } from '@/hooks/useMetrics'
import { cn } from '@/lib/utils'
import {
  BarChart3,
  Users,
  Timer,
  Zap,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  TrendingUp,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Target,
  Swords,
  Trophy,
  Lightbulb,
  Quote,
  Star,
  Sparkles,
  Award,
} from 'lucide-react'
import type { AgentEpisodeMetrics, ContentAnalysis, AgentInsight } from '@/types'

// ─── Safe Defaults ────────────────────────────────────────
// Firestore docs may have missing nested objects — ensure they always exist.

const EMPTY_FLOOR: AgentEpisodeMetrics['floor'] = { claims_attempted: 0, claims_won: 0, claims_lost: 0, claims_error: 0, claim_success_rate: 0, avg_hold_time_seconds: 0, hold_times: [], releases_with_post: 0, releases_without_post: 0, retries_scheduled: 0, retries_executed: 0, retries_succeeded: 0, recovery_signals_received: 0, recovery_claims_attempted: 0 }
const EMPTY_URGENCY: AgentEpisodeMetrics['urgency'] = { total_computations: 0, avg_urgency: 0, above_threshold_rate: 0, scores: [] }
const EMPTY_VALIDATION: AgentEpisodeMetrics['validation'] = { total_attempts: 0, passes: 0, failures: 0, pass_rate: 0, failure_types: {}, retries_per_turn: [] }
const EMPTY_GENERATION: AgentEpisodeMetrics['generation'] = { total_calls: 0, avg_time_seconds: 0, total_time_seconds: 0, times: [] }
const EMPTY_PARTICIPATION: AgentEpisodeMetrics['participation'] = { turns_posted: 0, turns_by_phase: {}, silence_durations: [], invitation_response_times: [] }

function safeAgent(a: AgentEpisodeMetrics): AgentEpisodeMetrics {
  return {
    ...a,
    floor: a.floor ? { ...EMPTY_FLOOR, ...a.floor } : { ...EMPTY_FLOOR },
    urgency: a.urgency ? { ...EMPTY_URGENCY, ...a.urgency } : { ...EMPTY_URGENCY },
    validation: a.validation ? { ...EMPTY_VALIDATION, ...a.validation } : { ...EMPTY_VALIDATION },
    generation: a.generation ? { ...EMPTY_GENERATION, ...a.generation } : { ...EMPTY_GENERATION },
    participation: a.participation ? { ...EMPTY_PARTICIPATION, ...a.participation } : { ...EMPTY_PARTICIPATION },
  }
}

// ─── Helpers ──────────────────────────────────────────────

function fmt(n: number, decimals = 2) {
  return n.toFixed(decimals)
}

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`
}

function duration(seconds: number) {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${s}s`
}

// ─── Color Maps ──────────────────────────────────────────

const AGENT_COLORS: Record<string, string> = {
  Iron: 'bg-slate-500',
  Nova: 'bg-violet-500',
  Root: 'bg-emerald-500',
  Logic: 'bg-blue-500',
  Spark: 'bg-amber-500',
  Zenith: 'bg-cyan-500',
}

const AGENT_TEXT_COLORS: Record<string, string> = {
  Iron: 'text-slate-600 dark:text-slate-400',
  Nova: 'text-violet-600 dark:text-violet-400',
  Root: 'text-emerald-600 dark:text-emerald-400',
  Logic: 'text-blue-600 dark:text-blue-400',
  Spark: 'text-amber-600 dark:text-amber-400',
  Zenith: 'text-cyan-600 dark:text-cyan-400',
}

const PHASE_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  INTRO:          { bg: 'bg-gray-100 dark:bg-gray-800',     text: 'text-gray-700 dark:text-gray-300',     bar: 'bg-gray-400 dark:bg-gray-500' },
  OPENING_ROUND:  { bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', bar: 'bg-indigo-400 dark:bg-indigo-500' },
  DISCUSSION:     { bg: 'bg-blue-50 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-300',     bar: 'bg-blue-400 dark:bg-blue-500' },
  SCENARIO:       { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300',   bar: 'bg-amber-400 dark:bg-amber-500' },
  TRADEOFF_VOTE:  { bg: 'bg-violet-50 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', bar: 'bg-violet-400 dark:bg-violet-500' },
  CLOSING:        { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', bar: 'bg-emerald-400 dark:bg-emerald-500' },
}

const PHASE_ORDER = ['INTRO', 'OPENING_ROUND', 'DISCUSSION', 'SCENARIO', 'TRADEOFF_VOTE', 'CLOSING']

function phaseColor(phase: string) {
  return PHASE_COLORS[phase] || PHASE_COLORS.INTRO
}

// ─── Reusable Components ─────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'text-primary-600 dark:text-primary-400',
}: {
  label: string
  value: string | number
  sub?: string
  icon: React.ElementType
  color?: string
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {label}
        </span>
        <Icon className={cn('w-4 h-4', color)} />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

function MiniStat({
  label,
  value,
  good,
  bad,
}: {
  label: string
  value: string | number
  good?: boolean
  bad?: boolean
}) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
      <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{label}</p>
      <p
        className={cn(
          'text-sm font-semibold',
          good && 'text-emerald-600 dark:text-emerald-400',
          bad && 'text-red-600 dark:text-red-400',
          !good && !bad && 'text-gray-900 dark:text-white'
        )}
      >
        {value}
      </p>
    </div>
  )
}

function HorizontalBar({
  items,
  maxValue,
}: {
  items: { label: string; value: number; color: string }[]
  maxValue: number
}) {
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <span className="w-16 text-xs font-medium text-gray-600 dark:text-gray-400 text-right truncate">
            {item.label}
          </span>
          <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-5 relative overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all duration-500', item.color)}
              style={{ width: maxValue > 0 ? `${(item.value / maxValue) * 100}%` : '0%' }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-300">
              {item.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

function GiniGauge({ value }: { value: number }) {
  const angle = value * 180
  const label =
    value < 0.1 ? 'Excellent' : value < 0.2 ? 'Good' : value < 0.35 ? 'Fair' : 'Poor'
  const color =
    value < 0.1
      ? 'text-emerald-500'
      : value < 0.2
        ? 'text-blue-500'
        : value < 0.35
          ? 'text-amber-500'
          : 'text-red-500'

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-16 overflow-hidden">
        <div className="absolute inset-0 rounded-t-full border-4 border-gray-200 dark:border-gray-700" />
        <div
          className="absolute bottom-0 left-1/2 w-1 h-14 bg-gray-900 dark:bg-white origin-bottom rounded-full transition-transform duration-700"
          style={{ transform: `translateX(-50%) rotate(${angle - 90}deg)` }}
        />
        <div className="absolute bottom-0 left-1/2 w-3 h-3 -translate-x-1/2 translate-y-1/2 rounded-full bg-gray-900 dark:bg-white" />
      </div>
      <p className={cn('text-2xl font-bold mt-2', color)}>{fmt(value, 4)}</p>
      <p className={cn('text-xs font-medium', color)}>{label} Balance</p>
      <p className="text-[10px] text-gray-400 mt-1">0 = perfect, 1 = one agent dominates</p>
    </div>
  )
}

// ─── Tab Button ──────────────────────────────────────────

function TabButton({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean
  label: string
  icon: React.ElementType
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all',
        active
          ? 'bg-primary-500 text-white shadow-md'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
      )}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

// ─── Overview Tab ────────────────────────────────────────

function OverviewTab({
  summary,
  modMetrics,
  modLoading,
  agentMetrics,
}: {
  summary: any
  modMetrics: any
  modLoading: boolean
  agentMetrics: AgentEpisodeMetrics[] | undefined
}) {
  // Build per-agent per-phase matrix from agent metrics (used if moderator metrics not available)
  const agentPhaseMatrix = useMemo(() => {
    if (!agentMetrics) return null
    const phases = new Set<string>()
    const matrix: Record<string, Record<string, number>> = {}
    agentMetrics.forEach((a) => {
      const tbp = a.participation?.turns_by_phase ?? {}
      matrix[a.agent_name] = { ...tbp }
      Object.keys(tbp).forEach((p) => phases.add(p))
    })
    return { phases: PHASE_ORDER.filter((p) => phases.has(p)), matrix }
  }, [agentMetrics])

  return (
    <div className="space-y-4">
      {/* Overview Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Duration" value={duration(summary.episode_duration_seconds)} icon={Timer} />
          <StatCard label="Agent Turns" value={summary.total_agent_turns} icon={Users} color="text-emerald-500" />
          <StatCard
            label="Interventions"
            value={summary.total_interventions}
            sub={`Rate: ${pct(summary.intervention_rate)}`}
            icon={AlertTriangle}
            color="text-amber-500"
          />
          <StatCard
            label="Gini"
            value={fmt(summary.gini_coefficient, 4)}
            sub={summary.gini_coefficient < 0.15 ? 'Well balanced' : 'Imbalanced'}
            icon={BarChart3}
            color={summary.gini_coefficient < 0.15 ? 'text-emerald-500' : 'text-red-500'}
          />
        </div>
      )}

      {/* Moderator Metrics */}
      {modLoading ? (
        <div className="card p-8 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
          <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      ) : modMetrics ? (
        <div className="space-y-4">
          {/* Participation Balance */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-500" />
              Participation Balance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <GiniGauge value={modMetrics.participation.gini} />
              <HorizontalBar
                items={Object.entries(modMetrics.participation.per_agent).map(
                  ([name, count]) => ({
                    label: name,
                    value: count as number,
                    color: AGENT_COLORS[name] || 'bg-gray-400',
                  })
                )}
                maxValue={Math.max(...Object.values(modMetrics.participation.per_agent).map(Number), 1)}
              />
            </div>
          </div>

          {/* Phase Timeline — color-coded with per-agent breakdown */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-500" />
              Phase Timeline
            </h3>
            <div className="space-y-3">
              {PHASE_ORDER.filter((p) => modMetrics.phases.durations_seconds[p] != null).map(
                (phase) => {
                  const dur = modMetrics.phases.durations_seconds[phase] || 0
                  const turns = modMetrics.phases.turn_counts[phase] || 0
                  const totalDur = Object.values(modMetrics.phases.durations_seconds as Record<string, number>).reduce(
                    (a: number, b: number) => a + b,
                    0
                  )
                  const widthPct = totalDur > 0 ? (dur / totalDur) * 100 : 0
                  const pc = phaseColor(phase)

                  // Per-agent turns in this phase
                  const agentTurnsInPhase: { name: string; count: number }[] = []
                  if (agentMetrics) {
                    agentMetrics.forEach((a) => {
                      const c = a.participation?.turns_by_phase?.[phase]
                      if (c) agentTurnsInPhase.push({ name: a.agent_name, count: c })
                    })
                  }

                  return (
                    <div key={phase}>
                      <div className="flex items-center gap-3">
                        <span className={cn('w-32 text-xs font-medium text-right px-2 py-0.5 rounded', pc.bg, pc.text)}>
                          {phase.replace('_', ' ')}
                        </span>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-7 relative overflow-hidden">
                          <div
                            className={cn('h-full rounded-full transition-all duration-500', pc.bar)}
                            style={{ width: `${Math.max(widthPct, 3)}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                            {duration(dur)} &middot; {turns} turns
                          </span>
                        </div>
                      </div>
                      {/* Agent dots for this phase */}
                      {agentTurnsInPhase.length > 0 && (
                        <div className="ml-36 mt-1 flex items-center gap-1.5 flex-wrap">
                          {agentTurnsInPhase
                            .sort((a, b) => b.count - a.count)
                            .map((at) => (
                              <span
                                key={at.name}
                                className={cn(
                                  'inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded',
                                  AGENT_TEXT_COLORS[at.name] || 'text-gray-500'
                                )}
                              >
                                <span className={cn('w-1.5 h-1.5 rounded-full', AGENT_COLORS[at.name] || 'bg-gray-400')} />
                                {at.name} {at.count}
                              </span>
                            ))}
                        </div>
                      )}
                    </div>
                  )
                }
              )}
            </div>
          </div>

          {/* Interventions */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Moderator Interventions
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-auto">
                Rate: {pct(modMetrics.interventions.intervention_rate)} of agent turns
              </span>
            </h3>
            {modMetrics.interventions.total === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No interventions needed this episode
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(modMetrics.interventions.by_type).map(([type, count]) => (
                    <span
                      key={type}
                      className={cn(
                        'px-3 py-1 rounded-full text-xs font-medium',
                        type === 'escalation' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                        type === 'dominance' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                        type === 'silence' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                        !['escalation', 'dominance', 'silence'].includes(type) &&
                          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      )}
                    >
                      {type}: {count as number}
                    </span>
                  ))}
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {modMetrics.interventions.details.map((d: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 py-1"
                    >
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px]', phaseColor(d.phase).bg, phaseColor(d.phase).text)}>
                        {d.phase}
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-300" />
                      <span className="font-medium">{d.type}</span>
                      {d.target_agent && (
                        <span className={cn('font-semibold', AGENT_TEXT_COLORS[d.target_agent])}>
                          {d.target_agent}
                        </span>
                      )}
                      {d.trigger_reason && (
                        <span className="text-gray-400 italic truncate max-w-[200px]">{d.trigger_reason}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Opening Round */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary-500" />
              Opening Round
            </h3>
            <div className="flex items-center gap-2 flex-wrap">
              {modMetrics.opening_round.order.map((name: string, i: number) => (
                <div key={i} className="flex items-center gap-1">
                  {i > 0 && <ArrowRight className="w-3 h-3 text-gray-300" />}
                  <span
                    className={cn(
                      'px-2 py-1 rounded-lg text-xs font-semibold',
                      AGENT_COLORS[name]
                        ? `${AGENT_COLORS[name]}/10 ${AGENT_TEXT_COLORS[name]}`
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    )}
                  >
                    {i + 1}. {name}
                  </span>
                </div>
              ))}
            </div>
            {modMetrics.opening_round.out_of_turn_count > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                {modMetrics.opening_round.out_of_turn_count} out-of-turn speakers
              </p>
            )}
            {modMetrics.opening_round.agents_skipped.length > 0 && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Skipped: {modMetrics.opening_round.agents_skipped.join(', ')}
              </p>
            )}
          </div>
        </div>
      ) : (
        /* No moderator metrics — show phase breakdown from agent data if available */
        agentPhaseMatrix ? (
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-500" />
              Phase Participation
              <span className="text-xs font-normal text-gray-400 ml-2">(from agent metrics)</span>
            </h3>
            <div className="space-y-3">
              {agentPhaseMatrix.phases.map((phase) => {
                const pc = phaseColor(phase)
                const agentTurns = Object.entries(agentPhaseMatrix.matrix)
                  .map(([name, phases]) => ({ name, count: phases[phase] || 0 }))
                  .filter((a) => a.count > 0)
                  .sort((a, b) => b.count - a.count)
                const totalTurns = agentTurns.reduce((s, a) => s + a.count, 0)

                return (
                  <div key={phase} className="flex items-start gap-3">
                    <span className={cn('w-32 text-xs font-medium text-right px-2 py-0.5 rounded shrink-0', pc.bg, pc.text)}>
                      {phase.replace('_', ' ')}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {agentTurns.map((at) => (
                          <span
                            key={at.name}
                            className={cn(
                              'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full',
                              AGENT_COLORS[at.name] ? `${AGENT_COLORS[at.name]}/10` : 'bg-gray-100',
                              AGENT_TEXT_COLORS[at.name] || 'text-gray-500'
                            )}
                          >
                            <span className={cn('w-1.5 h-1.5 rounded-full', AGENT_COLORS[at.name] || 'bg-gray-400')} />
                            {at.name} {at.count}
                          </span>
                        ))}
                        <span className="text-[10px] text-gray-400 ml-1">{totalTurns} total</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No moderator metrics found for this session.</p>
            <p className="text-xs mt-1">Metrics are exported when the episode ends.</p>
          </div>
        )
      )}
    </div>
  )
}

// ─── Floor Control Tab ───────────────────────────────────

function FloorControlTab({ agentMetrics }: { agentMetrics: AgentEpisodeMetrics[] | undefined }) {
  if (!agentMetrics || agentMetrics.length === 0) {
    return (
      <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
        <Swords className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No agent metrics available for floor analysis.</p>
      </div>
    )
  }

  const sorted = [...agentMetrics].sort((a, b) => b.floor.claims_attempted - a.floor.claims_attempted)
  const maxClaims = Math.max(...sorted.map((a) => a.floor.claims_attempted), 1)
  const sortedBySuccess = [...agentMetrics].sort(
    (a, b) => a.floor.claim_success_rate - b.floor.claim_success_rate
  )

  return (
    <div className="space-y-4">
      {/* Floor Contention — Who Wanted to Speak */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <Swords className="w-4 h-4 text-red-500" />
          Floor Contention — Who Tried vs Who Succeeded
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Each bar shows total floor claim attempts. Green = won, Red = lost.
        </p>
        <div className="space-y-3">
          {sorted.map((agent) => {
            const name = agent.agent_name
            const attempted = agent.floor.claims_attempted
            const won = agent.floor.claims_won
            const lost = agent.floor.claims_lost

            return (
              <div key={name}>
                <div className="flex items-center gap-3">
                  <div className="w-16 flex items-center gap-1.5 justify-end">
                    <span className={cn('w-2.5 h-2.5 rounded-full', AGENT_COLORS[name] || 'bg-gray-400')} />
                    <span className={cn('text-xs font-semibold', AGENT_TEXT_COLORS[name] || 'text-gray-600')}>
                      {name}
                    </span>
                  </div>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-6 relative overflow-hidden">
                    {/* Stacked: green for won, red for lost */}
                    <div className="h-full flex">
                      <div
                        className="h-full bg-emerald-400 dark:bg-emerald-500 transition-all duration-500"
                        style={{ width: `${(won / maxClaims) * 100}%` }}
                      />
                      <div
                        className="h-full bg-red-300 dark:bg-red-500/60 transition-all duration-500"
                        style={{ width: `${(lost / maxClaims) * 100}%` }}
                      />
                    </div>
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700 dark:text-gray-200">
                      {won} won / {lost} lost ({pct(won / Math.max(attempted, 1))})
                    </span>
                  </div>
                  <span className="w-8 text-xs text-gray-500 text-right">{attempted}</span>
                </div>
              </div>
            )
          })}
        </div>
        <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-400" /> Won</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-300" /> Lost</span>
          <span className="ml-auto">Right column = total attempts</span>
        </div>
      </div>

      {/* Success Rate Ranking */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <Target className="w-4 h-4 text-blue-500" />
          Floor Success Rate — Starvation Ranking
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Sorted lowest to highest. Low success rate = agent is being starved.
        </p>
        <div className="space-y-2">
          {sortedBySuccess.map((agent) => {
            const name = agent.agent_name
            const rate = agent.floor.claim_success_rate
            const isStarved = rate < 0.15
            const isFair = rate >= 0.15 && rate < 0.30

            return (
              <div key={name} className="flex items-center gap-3">
                <div className="w-16 flex items-center gap-1.5 justify-end">
                  <span className={cn('w-2 h-2 rounded-full', AGENT_COLORS[name] || 'bg-gray-400')} />
                  <span className={cn('text-xs font-medium', AGENT_TEXT_COLORS[name] || 'text-gray-600')}>
                    {name}
                  </span>
                </div>
                <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-4 relative overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      isStarved ? 'bg-red-400 dark:bg-red-500' : isFair ? 'bg-amber-400 dark:bg-amber-500' : 'bg-emerald-400 dark:bg-emerald-500'
                    )}
                    style={{ width: `${rate * 100}%` }}
                  />
                </div>
                <span
                  className={cn(
                    'w-14 text-xs font-semibold text-right',
                    isStarved ? 'text-red-600 dark:text-red-400' : isFair ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
                  )}
                >
                  {pct(rate)}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Urgency vs Success — who wanted it most but got it least */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
          <Activity className="w-4 h-4 text-violet-500" />
          Urgency vs Success
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Agents with high urgency but low success rate are being starved.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2 font-medium">Agent</th>
                <th className="text-right py-2 font-medium">Avg Urgency</th>
                <th className="text-right py-2 font-medium">Above Threshold</th>
                <th className="text-right py-2 font-medium">Floor Win Rate</th>
                <th className="text-right py-2 font-medium">Turns Posted</th>
                <th className="text-right py-2 font-medium">Retries</th>
              </tr>
            </thead>
            <tbody>
              {[...agentMetrics]
                .sort((a, b) => b.urgency.avg_urgency - a.urgency.avg_urgency)
                .map((agent) => {
                  const name = agent.agent_name
                  const starved =
                    agent.urgency.avg_urgency > 0.8 && agent.floor.claim_success_rate < 0.15
                  return (
                    <tr
                      key={name}
                      className={cn(
                        'border-b border-gray-50 dark:border-gray-800/50',
                        starved && 'bg-red-50/50 dark:bg-red-900/10'
                      )}
                    >
                      <td className="py-2">
                        <span className="flex items-center gap-1.5">
                          <span className={cn('w-2 h-2 rounded-full', AGENT_COLORS[name] || 'bg-gray-400')} />
                          <span className={cn('font-semibold', AGENT_TEXT_COLORS[name])}>{name}</span>
                          {starved && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                              STARVED
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="text-right py-2 font-mono">{fmt(agent.urgency.avg_urgency, 3)}</td>
                      <td className="text-right py-2">{pct(agent.urgency.above_threshold_rate)}</td>
                      <td
                        className={cn(
                          'text-right py-2 font-semibold',
                          agent.floor.claim_success_rate < 0.15
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-emerald-600 dark:text-emerald-400'
                        )}
                      >
                        {pct(agent.floor.claim_success_rate)}
                      </td>
                      <td className="text-right py-2">{agent.participation.turns_posted}</td>
                      <td className="text-right py-2">{agent.floor.retries_scheduled}</td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Retry & Recovery Stats */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-secondary-500" />
          Aggregate Floor Control
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="Total Claims" value={agentMetrics.reduce((s, a) => s + a.floor.claims_attempted, 0)} />
          <MiniStat label="Total Won" value={agentMetrics.reduce((s, a) => s + a.floor.claims_won, 0)} good />
          <MiniStat
            label="Total Collisions"
            value={agentMetrics.reduce((s, a) => s + a.floor.claims_lost, 0)}
            bad={agentMetrics.reduce((s, a) => s + a.floor.claims_lost, 0) > 0}
          />
          <MiniStat
            label="Failed Releases"
            value={agentMetrics.reduce((s, a) => s + a.floor.releases_without_post, 0)}
            bad={agentMetrics.reduce((s, a) => s + a.floor.releases_without_post, 0) > 0}
          />
          <MiniStat label="Retries Scheduled" value={agentMetrics.reduce((s, a) => s + a.floor.retries_scheduled, 0)} />
          <MiniStat label="Retries Succeeded" value={agentMetrics.reduce((s, a) => s + a.floor.retries_succeeded, 0)} good />
          <MiniStat label="Recovery Signals" value={agentMetrics.reduce((s, a) => s + a.floor.recovery_signals_received, 0)} />
          <MiniStat
            label="Avg LLM Latency"
            value={`${fmt(agentMetrics.reduce((s, a) => s + a.generation.avg_time_seconds, 0) / agentMetrics.length)}s`}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Agent Detail Panel ──────────────────────────────────

function AgentDetailPanel({ agent }: { agent: AgentEpisodeMetrics }) {
  const [expanded, setExpanded] = useState(false)
  const name = agent.agent_name

  // Sort phases in correct order
  const phaseEntries = Object.entries(agent.participation?.turns_by_phase ?? {}).sort(
    (a, b) => PHASE_ORDER.indexOf(a[0]) - PHASE_ORDER.indexOf(b[0])
  )
  const totalPhaseTurns = phaseEntries.reduce((s, [, c]) => s + c, 0)

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={cn('w-3 h-3 rounded-full', AGENT_COLORS[name] || 'bg-gray-400')} />
          <span className={cn('font-semibold', AGENT_TEXT_COLORS[name] || 'text-gray-700 dark:text-gray-300')}>
            {name}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {agent.participation.turns_posted} turns
          </span>
          {/* Mini success rate badge */}
          <span
            className={cn(
              'text-[10px] font-medium px-1.5 py-0.5 rounded',
              agent.floor.claim_success_rate < 0.15
                ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : agent.floor.claim_success_rate < 0.30
                  ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
            )}
          >
            {pct(agent.floor.claim_success_rate)} floor rate
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800">
          {/* Turns by Phase — visual bar */}
          {phaseEntries.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Turns by Phase
              </h4>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-5 flex overflow-hidden">
                {phaseEntries.map(([phase, count]) => {
                  const pc = phaseColor(phase)
                  const w = totalPhaseTurns > 0 ? (count / totalPhaseTurns) * 100 : 0
                  return (
                    <div
                      key={phase}
                      className={cn('h-full flex items-center justify-center text-[9px] font-bold', pc.bar)}
                      style={{ width: `${w}%` }}
                      title={`${phase}: ${count} turns`}
                    >
                      {w > 12 && <span className="text-white/90">{count}</span>}
                    </div>
                  )
                })}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {phaseEntries.map(([phase, count]) => {
                  const pc = phaseColor(phase)
                  return (
                    <span key={phase} className={cn('text-[9px] font-medium px-1.5 py-0.5 rounded', pc.bg, pc.text)}>
                      {phase.replace('_', ' ')}: {count}
                    </span>
                  )
                })}
              </div>
            </div>
          )}

          {/* Floor Control */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Floor Control
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MiniStat label="Claims" value={agent.floor.claims_attempted} />
              <MiniStat label="Won" value={agent.floor.claims_won} good />
              <MiniStat label="Lost" value={agent.floor.claims_lost} bad={agent.floor.claims_lost > 0} />
              <MiniStat label="Success" value={pct(agent.floor.claim_success_rate)} />
              <MiniStat label="Avg Hold" value={duration(agent.floor.avg_hold_time_seconds)} />
              <MiniStat label="Released w/o Post" value={agent.floor.releases_without_post} bad={agent.floor.releases_without_post > 0} />
              <MiniStat label="Retries Scheduled" value={agent.floor.retries_scheduled} />
              <MiniStat label="Recovery Signals" value={agent.floor.recovery_signals_received} />
            </div>
          </div>

          {/* Urgency */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Urgency Scoring
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <MiniStat label="Computations" value={agent.urgency.total_computations} />
              <MiniStat label="Avg Urgency" value={fmt(agent.urgency.avg_urgency, 3)} />
              <MiniStat label="Above Threshold" value={pct(agent.urgency.above_threshold_rate)} />
            </div>
          </div>

          {/* Validation */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Self-Validation
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MiniStat label="Attempts" value={agent.validation.total_attempts} />
              <MiniStat label="Passes" value={agent.validation.passes} good />
              <MiniStat label="Failures" value={agent.validation.failures} bad={agent.validation.failures > 0} />
              <MiniStat label="Pass Rate" value={pct(agent.validation.pass_rate)} good={agent.validation.pass_rate > 0.8} />
            </div>
            {Object.keys(agent.validation.failure_types).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {Object.entries(agent.validation.failure_types).map(([type, count]) => (
                  <span
                    key={type}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  >
                    {type}: {count}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Generation */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              LLM Generation
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <MiniStat label="Total Calls" value={agent.generation.total_calls} />
              <MiniStat label="Avg Latency" value={`${fmt(agent.generation.avg_time_seconds)}s`} />
              <MiniStat label="Total Time" value={duration(agent.generation.total_time_seconds)} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Agent Details Tab ───────────────────────────────────

function AgentDetailsTab({ agentMetrics, agentsLoading }: { agentMetrics: AgentEpisodeMetrics[] | undefined; agentsLoading: boolean }) {
  return (
    <div className="space-y-4">
      {/* Validation Summary */}
      {agentMetrics && agentMetrics.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
            Self-Validation Summary
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Total Attempts" value={agentMetrics.reduce((s, a) => s + a.validation.total_attempts, 0)} />
            <MiniStat label="Total Passes" value={agentMetrics.reduce((s, a) => s + a.validation.passes, 0)} good />
            <MiniStat
              label="Total Failures"
              value={agentMetrics.reduce((s, a) => s + a.validation.failures, 0)}
              bad={agentMetrics.reduce((s, a) => s + a.validation.failures, 0) > 0}
            />
            <MiniStat
              label="Overall Pass Rate"
              value={pct(
                agentMetrics.reduce((s, a) => s + a.validation.passes, 0) /
                  Math.max(agentMetrics.reduce((s, a) => s + a.validation.total_attempts, 0), 1)
              )}
              good
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {(() => {
              const allFailures: Record<string, number> = {}
              agentMetrics.forEach((a) => {
                Object.entries(a.validation.failure_types).forEach(([type, count]) => {
                  allFailures[type] = (allFailures[type] || 0) + count
                })
              })
              return Object.entries(allFailures)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <span
                    key={type}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                  >
                    {type}: {count}
                  </span>
                ))
            })()}
          </div>
        </div>
      )}

      {/* Per-Agent Panels */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-secondary-500" />
          Per-Agent Metrics
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
            Click to expand
          </span>
        </h3>
        {agentsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card h-14 animate-pulse" />
            ))}
          </div>
        ) : agentMetrics && agentMetrics.length > 0 ? (
          <div className="space-y-2">
            {agentMetrics
              .sort((a, b) => b.participation.turns_posted - a.participation.turns_posted)
              .map((agent) => (
                <AgentDetailPanel key={agent.agent_name} agent={agent} />
              ))}
          </div>
        ) : (
          <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No agent metrics found for this session.</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Insights Tab ────────────────────────────────────────

const AWARD_COLORS: Record<string, { bg: string; text: string; icon: string }> = {
  'Most Insightful': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', icon: 'text-blue-500' },
  'Best Challenger': { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', icon: 'text-red-500' },
  'Most Practical': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-500' },
}

function InsightAgentCard({ insight }: { insight: AgentInsight }) {
  const [expanded, setExpanded] = useState(false)
  const name = insight.agent_name

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={cn('w-3 h-3 rounded-full', AGENT_COLORS[name] || 'bg-gray-400')} />
          <span className={cn('font-semibold', AGENT_TEXT_COLORS[name] || 'text-gray-700 dark:text-gray-300')}>
            {name}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {insight.turns_posted} turns
          </span>
          <span className="text-xs text-violet-600 dark:text-violet-400 italic hidden sm:inline">
            {insight.unique_angle?.slice(0, 50)}{insight.unique_angle?.length > 50 ? '...' : ''}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-800">
          {/* Key Points */}
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5" />
              Key Points
            </h4>
            <ul className="space-y-1.5">
              {insight.key_points.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className={cn('mt-1.5 w-1.5 h-1.5 rounded-full shrink-0', AGENT_COLORS[name] || 'bg-gray-400')} />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Unique Angle */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Unique Angle
            </h4>
            <p className="text-sm text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3">
              {insight.unique_angle}
            </p>
          </div>

          {/* Notable Quote */}
          {insight.notable_quote && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Quote className="w-3.5 h-3.5" />
                Notable Quote
              </h4>
              <blockquote className={cn(
                'text-sm italic text-gray-700 dark:text-gray-300 border-l-4 pl-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-r-lg',
                name === 'Iron' && 'border-slate-400',
                name === 'Nova' && 'border-violet-400',
                name === 'Root' && 'border-emerald-400',
                name === 'Logic' && 'border-blue-400',
                name === 'Spark' && 'border-amber-400',
                name === 'Zenith' && 'border-cyan-400',
                !['Iron', 'Nova', 'Root', 'Logic', 'Spark', 'Zenith'].includes(name) && 'border-gray-400'
              )}>
                "{insight.notable_quote}"
              </blockquote>
            </div>
          )}

          {/* Style Note */}
          {insight.style_note && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5" />
                Style
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{insight.style_note}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function InsightsTab({ analysis, isLoading }: { analysis: ContentAnalysis | null | undefined; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="card p-8 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
          <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
        <div className="card p-8 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4" />
          <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      </div>
    )
  }

  if (!analysis) {
    return (
      <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
        <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No content analysis available for this session.</p>
        <p className="text-xs mt-1">Content analysis is generated automatically when the episode ends.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Episode Summary */}
      <div className="card p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary-500" />
          Episode Summary
        </h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {analysis.topic_summary}
        </p>
      </div>

      {/* Speaker of the Podcast — Hero Card */}
      <div className="card overflow-hidden">
        <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 p-5">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-amber-100 uppercase tracking-wider">
                Speaker of the Podcast
              </p>
              <p className="text-2xl font-bold text-white flex items-center gap-2">
                {analysis.overall_speaker}
                <span className={cn('w-3 h-3 rounded-full', AGENT_COLORS[analysis.overall_speaker] || 'bg-white/50')} />
              </p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-amber-50 dark:bg-amber-900/10">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {analysis.overall_speaker_reason}
          </p>
        </div>
      </div>

      {/* Agent Key Points */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-violet-500" />
          Agent Contributions
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
            Click to expand
          </span>
        </h3>
        <div className="space-y-2">
          {analysis.agent_insights.map((insight) => (
            <InsightAgentCard key={insight.agent_name} insight={insight} />
          ))}
        </div>
      </div>

      {/* Episode Highlights */}
      {analysis.highlights && analysis.highlights.length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Episode Highlights
          </h3>
          <div className="relative pl-6 space-y-4">
            {/* Vertical timeline line */}
            <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-gray-200 dark:bg-gray-700" />
            {analysis.highlights.map((hl, i) => {
              const pc = phaseColor(hl.phase)
              return (
                <div key={i} className="relative">
                  {/* Timeline dot */}
                  <div className={cn(
                    'absolute -left-4 top-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900',
                    hl.significance === 'high' ? 'bg-amber-400' : 'bg-gray-300 dark:bg-gray-600'
                  )} />
                  <div className="flex items-start gap-2">
                    <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0', pc.bg, pc.text)}>
                      {hl.phase.replace('_', ' ')}
                    </span>
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{hl.moment}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {hl.agents_involved.map((agent) => (
                          <span
                            key={agent}
                            className={cn(
                              'text-[9px] font-medium px-1.5 py-0.5 rounded-full',
                              AGENT_TEXT_COLORS[agent] || 'text-gray-500'
                            )}
                          >
                            <span className={cn('inline-block w-1.5 h-1.5 rounded-full mr-0.5 align-middle', AGENT_COLORS[agent] || 'bg-gray-400')} />
                            {agent}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Awards Grid */}
      {analysis.awards && analysis.awards.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-500" />
            Episode Awards
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {analysis.awards.map((award, i) => {
              const awardStyle = AWARD_COLORS[award.category] || {
                bg: 'bg-purple-50 dark:bg-purple-900/20',
                text: 'text-purple-700 dark:text-purple-300',
                icon: 'text-purple-500',
              }
              return (
                <div key={i} className={cn('card p-4', awardStyle.bg)}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className={cn('w-4 h-4', awardStyle.icon)} />
                    <span className={cn('text-xs font-semibold uppercase tracking-wider', awardStyle.text)}>
                      {award.category}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    {award.agent}
                    <span className={cn('w-2.5 h-2.5 rounded-full', AGENT_COLORS[award.agent] || 'bg-gray-400')} />
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{award.reason}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────

type TabId = 'overview' | 'floor' | 'agents' | 'insights'

export default function MetricsPage() {
  const { isAdmin } = useAuthStore()
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabId>('overview')

  const { data: sessions, isLoading: sessionsLoading } = useMetricsSessions()
  const { data: modMetrics, isLoading: modLoading } = useModeratorMetrics(selectedSession)
  const { data: rawAgentMetrics, isLoading: agentsLoading } = useAgentMetrics(selectedSession)
  const agentMetrics = useMemo(() => rawAgentMetrics?.map(safeAgent), [rawAgentMetrics])
  const { data: contentAnalysis, isLoading: contentLoading } = useContentAnalysis(selectedSession)

  // Admin gate
  if (!isAdmin()) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Shield className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Admin Access Required</h2>
        <p className="text-gray-500 dark:text-gray-400">This page is only accessible to administrators.</p>
      </div>
    )
  }

  // Auto-select first session if none selected
  if (!selectedSession && sessions && sessions.length > 0) {
    const firstWithMetrics = sessions.find((s) => s.metrics_summary)
    if (firstWithMetrics) {
      setSelectedSession(firstWithMetrics.id)
    } else {
      setSelectedSession(sessions[0].id)
    }
  }

  const summary = sessions?.find((s) => s.id === selectedSession)?.metrics_summary

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary-500" />
            Episode Metrics
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Research instrumentation for floor control analysis
          </p>
        </div>
      </div>

      {/* Session Selector */}
      <div className="card p-4">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-2">
          Select Episode
        </label>
        {sessionsLoading ? (
          <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
        ) : (
          <select
            value={selectedSession || ''}
            onChange={(e) => {
              setSelectedSession(e.target.value || null)
              setActiveTab('overview')
            }}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Select a session...</option>
            {sessions?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.topic?.slice(0, 60) || s.id} — {s.created_at.toLocaleDateString()}{' '}
                {s.metrics_summary ? '(has metrics)' : '(no metrics yet)'}
              </option>
            ))}
          </select>
        )}
      </div>

      {!selectedSession && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Select an episode above to view metrics.</p>
          <p className="text-xs mt-1">Metrics are collected automatically during each podcast episode.</p>
        </div>
      )}

      {selectedSession && (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900/50 p-1 rounded-xl">
            <TabButton
              active={activeTab === 'overview'}
              label="Overview"
              icon={BarChart3}
              onClick={() => setActiveTab('overview')}
            />
            <TabButton
              active={activeTab === 'floor'}
              label="Floor Control"
              icon={Swords}
              onClick={() => setActiveTab('floor')}
            />
            <TabButton
              active={activeTab === 'agents'}
              label="Agent Details"
              icon={Users}
              onClick={() => setActiveTab('agents')}
            />
            <TabButton
              active={activeTab === 'insights'}
              label="Insights"
              icon={Lightbulb}
              onClick={() => setActiveTab('insights')}
            />
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <OverviewTab
              summary={summary}
              modMetrics={modMetrics}
              modLoading={modLoading}
              agentMetrics={agentMetrics}
            />
          )}
          {activeTab === 'floor' && <FloorControlTab agentMetrics={agentMetrics} />}
          {activeTab === 'agents' && (
            <AgentDetailsTab agentMetrics={agentMetrics} agentsLoading={agentsLoading} />
          )}
          {activeTab === 'insights' && (
            <InsightsTab analysis={contentAnalysis} isLoading={contentLoading} />
          )}
        </>
      )}
    </div>
  )
}
